<template>
  <div class="notification-badge-container">
    <Button
      :class="buttonClass"
      :aria-label="useStore().notifications?.state?.unreadCount > 0 ? t('notifications.bell.ariaUnread', { count: useStore().notifications.state.unreadCount }) : t('notifications.bell.ariaNone')"
      :submit="() => toggleNotifications()"
      :counter="useStore().notifications?.state?.unreadCount > 0 ? (useStore().notifications.state.unreadCount > 99 ? '99+' : useStore().notifications.state.unreadCount) : null"
      :showSucces="false"
      :showLoader="false"
    >
      <Icon name="notifications" class="notification-icon" :class="iconClass" :color="fill"/>
    </Button>

    <Popup
      :title="t('notifications.bell.title')"
      @close-popup="closeNotifications"
      :isPopupOpen="isOpen"
      align="center right"
      class="bg-base h-min-100 w-100 w-max-30r pd-medium"
    >
      <div class="cols-1 gap-thin o-y-scroll">
        <div v-if="useStore().notifications?.state?.loading" class="notifications-loading">
          {{ t('notifications.bell.loading') }}
        </div>

        <div v-else-if="recentNotifications.length === 0" class="notifications-empty">
          {{ t('notifications.bell.empty') }}
        </div>

        <div v-else class="flex-column flex gap-thin">
          <notification-item
            v-for="notification in recentNotifications"
            :key="notification._id"
            :notification="notification"
            @click="handleNotificationClick(notification)"
          />

          <div class="flex-nowrap flex gap-thin">
            <button
              v-if="useStore().notifications?.state?.unreadCount > 0"
              class="w-100 button--card t-inverse radius-small  button"
              @click="useStore().notifications.actions.markAllAsRead()"
            >
              {{ t('notifications.bell.markAllRead') }}
            </button>

            <router-link v-if="notificationsPageTo" class="w-100 button--inverse t-inverse radius-small button" :to="notificationsPageTo" @click="closeNotifications">
              {{ t('notifications.bell.viewAll') }}
            </router-link>
          </div>

        </div>

      </div>
    </Popup>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import Popup from '@martyrs/src/design-system/components/Popup/Popup.vue';
import Button from '@martyrs/src/design-system/components/Button/Button.vue';

import NotificationItem from '../blocks/NotificationItem.vue';
import { auth } from '@martyrs/src/modules/auth/views/store/auth.js';
import { useStore } from '@martyrs/src/modules/core/views/store/core.store.js';
import Icon from '@martyrs/src/design-system/components/Icon/Icon.vue';

const { t } = useI18n();

const props = defineProps({
  maxNotifications: {
    type: Number,
    default: 10
  },
  fill: {
    type: String,
    default: 'rgb(var(--bg-base))'
  },
  // Классы кнопки-колокольчика и иконки — прокидываются из route.meta через
  // Header (header_notifications_class / header_notifications_icon_class),
  // по образцу header_search_class. Дефолты сохраняют текущий вид.
  buttonClass: {
    type: [String, Array, Object],
    default: 'pd-zero bg-transparent'
  },
  iconClass: {
    type: String,
    default: 'i-medium'
  }
});

// Get router and notification functionality
const router = useRouter();

// Колокольчик живёт в шапке и рисуется на страницах, где модуль notifications
// ещё не активирован: store.notifications там просто нет. Поэтому читаем через
// опциональную цепочку, без заглушки-объекта.

// Local state
const isOpen = ref(false);

// Computed properties
const recentNotifications = computed(() => {
  return (useStore().notifications?.state?.notifications || [])
    .slice()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, props.maxNotifications);
});

// Methods
const toggleNotifications = () => {
  isOpen.value = !isOpen.value;
};

const closeNotifications = () => {
  isOpen.value = false;
};

const handleNotificationClick = (notification) => {
  isOpen.value = false;

  if (notification._id) {
    // Handle in store: пометить прочитанной + перейти по metadata.url/route
    useStore().notifications?.actions.handleNotificationAction({
      notificationId: notification._id,
      ...notification.metadata
    });
  }
};

// Роут страницы «все уведомления» резолвим по имени, а не хардкодим путь:
// модуль может быть смонтирован и под /users/:_id (дефолт), и под любым другим
// родителем через options.routeUsers (например /dashboard/notifications).
const notificationsPageTo = computed(() => {
  const candidates = [
    { name: 'Notifications' },
    { name: 'Notifications', params: { _id: auth.state.user?._id } },
  ];
  for (const target of candidates) {
    try {
      router.resolve(target);
      return target;
    } catch (e) {
      // роут не зарегистрирован или не хватает params — пробуем следующий вариант
    }
  }
  return null;
});

// Lifecycle hooks
onMounted(() => {
  // Load notifications when component mounts
  const userId = auth.state.user._id;
  if (userId) {
    useStore().notifications?.actions.getNotifications(userId);
  }
});
</script>

<style scoped>
.notification-badge-container {
  position: relative;
  display: inline-block;
}

.notification-icon {
  font-size: 1.4rem;
}


.notifications-loading,
.notifications-empty {
  padding: 24px;
  text-align: center;
  color: rgb(var(--texfw-light));
}
</style>